import { useMemo, useState } from "react";
import { Box, Chip } from "@mui/material";
import { api } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import DataTable, { TwoLine } from "../components/DataTable";
import StatusChip from "../components/StatusChip";
import { EVIDENCE } from "../constants";
import { formatDate } from "../utils/format";

export default function EvidencePage() {
  const { clientId } = useSession();
  const [filter, setFilter] = useState("ALL");
  const state = useApiData(() => api.getEvidence(), [clientId]);

  const counts = useMemo(() => {
    const result = {};
    (state.data || []).forEach((e) => { result[e.status] = (result[e.status] || 0) + 1; });
    return result;
  }, [state.data]);

  const columns = [
    { key: "title", label: "Evidence", render: (e) => <TwoLine main={e.title} sub={e.type} /> },
    { key: "controlRef", label: "Control", render: (e) => <TwoLine main={e.controlRef} sub={e.frameworkCode} /> },
    { key: "owner", label: "Owner" },
    { key: "status", label: "Status", render: (e) => <StatusChip label={e.statusLabel} color={EVIDENCE[e.status].color} /> },
    { key: "collectedOn", label: "Collected", render: (e) => formatDate(e.collectedOn) },
    { key: "expiresOn", label: "Expires", render: (e) => formatDate(e.expiresOn) },
  ];

  return (
    <>
      <PageHeader
        title="Evidence"
        subtitle="Proof that controls work. Missing, expired and soon-to-expire items are listed first."
      />
      <DataState state={state}>
        {(rows) => (
          <SectionCard>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
              <Chip
                label={`All (${rows.length})`}
                clickable
                color={filter === "ALL" ? "primary" : "default"}
                variant={filter === "ALL" ? "filled" : "outlined"}
                onClick={() => setFilter("ALL")}
              />
              {Object.entries(EVIDENCE).map(([key, v]) => (
                <Chip
                  key={key}
                  label={`${v.label} (${counts[key] || 0})`}
                  clickable
                  color={filter === key ? "primary" : "default"}
                  variant={filter === key ? "filled" : "outlined"}
                  onClick={() => setFilter(key)}
                />
              ))}
            </Box>
            <DataTable
              columns={columns}
              rows={filter === "ALL" ? rows : rows.filter((e) => e.status === filter)}
              emptyText="No evidence in this category."
            />
          </SectionCard>
        )}
      </DataState>
    </>
  );
}
